/**
 * Guided-tour copy — {en, hi, ml} maps keyed by step (tour spec §8).
 *
 * English is verbatim from the spec's step tables. Hindi/Malayalam follow the
 * house rules: translated noun + English term on first mention ("केस फ़ाइल
 * (case file)"), धारा/വകുപ്പ് conventions (never "§"), and NO uppercase/
 * letter-spacing transforms on Indic text (breaks conjuncts).
 *
 * NOTE FOR REVIEW: the Hindi and Malayalam copy below is machine-drafted and
 * MUST be reviewed by a practicing advocate before ship — legal vocabulary
 * mistranslation is a trust risk with this persona (spec §8).
 *
 * Language resolves voice_lang (MicButton's saved choice) → navigator.language
 * → English. ~40 strings; deliberately no i18n framework.
 */

export type TourLang = 'en' | 'hi' | 'ml';

export interface StepCopy {
  title: string;
  body: string;
}

export interface TourChrome {
  next: string;
  prev: string;
  done: string;
  closeAria: string;
  /** driver.js progressText template — keeps {{current}}/{{total}} tokens. */
  progress: string;
  /** aria-live announcement: "Step {n} of {m}: {title}" */
  announceStep: (n: number, m: number, title: string) => string;
  resumePrompt: (n: number, m: number) => string;
  resume: string;
  notNow: string;
  skipTitle: string;
  skipBody: string;
  keepGoing: string;
  stopForNow: string;
  dontShowAgain: string;
  /** Skip-confirm third option — jump to the next chapter (cross-route tours). */
  skipChapter: string;
  /** Per-step progress template with the chapter name ("Research · 4 of 13").
   *  MUST keep driver.js's {{current}}/{{total}} tokens. */
  chapterProgress: (chapter: string) => string;
}

export interface TourStrings {
  chrome: TourChrome;
  /** Chapter display names, keyed by TourStepDef.chapter. */
  chapters: Record<string, string>;
  steps: Record<string, StepCopy>;
}

const en: TourStrings = {
  chrome: {
    next: 'Next',
    prev: 'Back',
    done: 'Done',
    closeAria: 'Close the tour',
    progress: 'Step {{current}} of {{total}}',
    announceStep: (n, m, title) => `Step ${n} of ${m}: ${title}`,
    resumePrompt: (n, m) => `Resume the tour? (step ${n} of ${m})`,
    resume: 'Resume',
    notNow: 'Not now',
    skipTitle: 'Stop the tour?',
    skipBody: 'Your place is saved — resume any time from Settings → Help.',
    keepGoing: 'Keep going',
    stopForNow: 'Stop for now',
    dontShowAgain: "Don't show tips again",
    skipChapter: 'Skip this part',
    chapterProgress: (chapter) => `${chapter} · {{current}} of {{total}}`,
  },
  chapters: {
    day: 'Your day',
    research: 'Research',
    drafting: 'Drafting',
    cases: 'Your cases',
    always: 'Always available',
  },
  steps: {
    'around.1': {
      title: 'Your mornings start here',
      body: "Today's and tomorrow's hearings, clients to call before court, and tasks falling due — gathered here every morning, like a cause list prepared for you. After court, log what happened and Urava writes the next hearing into your diary itself.",
    },
    'around.2': {
      title: 'Every kind of work starts here',
      body: 'One button for all new work: a fully cited research memorandum, any of 17 court documents, or a new case file. Attach FIRs, notices and judgments right inside the chat — Urava reads scanned and handwritten filings in Malayalam, Hindi and English.',
    },
    'around.3': {
      title: 'Describe it in your own words',
      body: 'Type the situation as you would tell a colleague — English, हिंदी or മലയാളം — or tap the mic and speak. Urava asks what matters, then frames the precise legal question worth researching. Nothing runs until you approve it.',
    },
    'around.4': {
      title: 'Hand it the papers',
      body: 'Attach the FIR, notice, agreement or judgment here. Urava reads scanned and handwritten documents and researches against what they actually say — dates, parties, amounts — not just your summary of them.',
    },
    'around.5': {
      title: 'Already know your question?',
      body: "You don't have to chat. The moment you've typed a question, a 'Research it exactly as you wrote it' button appears below the composer — one tap runs your words unchanged, no back-and-forth.",
    },
    'around.6': {
      title: "Every memo you've ever run",
      body: 'Your research library — searchable by question, memo text or case, whether or not it is filed under a case. Every memo arrives with its citations checked against live sources, and unverified ones are flagged so you know what to re-read.',
    },
    'around.7': {
      title: 'Pick a document, answer questions',
      body: 'Bail applications, Section 138 notices, plaints, written statements, consumer complaints, writ petitions — 17 court-ready formats. Pick one and Urava interviews you one question at a time, skipping anything you have already told it.',
    },
    'around.8': {
      title: 'Attach what you received',
      body: 'Replying to a notice or a plaint? Attach it during the interview and Urava extracts the parties, dates and amounts from the document itself — you are only asked for what is genuinely missing.',
    },
    'around.9': {
      title: 'One file per case',
      body: 'Each case holds its hearings, research, drafts, documents, tasks and people together — your physical case file, without the missing pages. Open a case and everything about it is in front of you.',
    },
    'around.10': {
      title: 'Filing is optional',
      body: 'A quick question never needs a case — research and drafts stand perfectly well on their own. When something becomes a real matter, create the case here and file earlier work under it with one tap. Nothing is lost either way.',
    },
    'around.11': {
      title: 'Ask from any page',
      body: 'Mid-draft, reading a memo, checking a case — this opens the research chat wherever you are. On desktop it docks beside your work, so the answer arrives without losing your place.',
    },
    'around.12': {
      title: 'What your plan includes',
      body: 'Your researches remaining this month, always visible here. Only successful research counts — a failed run is never charged to you. Drafting has its own separate allowance, and case tracking is never metered.',
    },
    'around.13': {
      title: 'Settings, help and WhatsApp',
      body: 'Your menu: Settings, this walkthrough any time you want it again, the full guide to how Urava works, and Urava on WhatsApp — ask questions and get hearing reminders from the number you already use every day.',
    },
    'memo.1': {
      title: 'Citations are checked',
      body: 'We verify every cited link. Unverified ones are flagged in the appendix. Always read the judgment yourself before relying on it.',
    },
    'memo.2': {
      title: 'Work with your memo',
      body: 'Discuss it, quiz yourself before the hearing, or let Urava argue the opposing side against you.',
    },
    'memo.3': {
      title: 'Turn research into a draft',
      body: 'Draft a notice or petition here — it cites only the authorities from this memo.',
    },
    'casefile.1': {
      title: 'One case, everything in it',
      body: 'Hearings, research, drafts, documents, tasks and people for this case — each in its own tab.',
    },
    'casefile.2': {
      title: 'Work from the case',
      body: 'Research this issue or draft for this case from here. The results attach to this case automatically.',
    },
    'casefile.3': {
      title: 'After court, log what happened',
      body: 'Enter the outcome and next date. Urava creates the next hearing in your diary for you.',
    },
    'drafting.1': {
      title: 'Pick the document',
      body: 'Notices, bail applications, plaints, replies — 17 types. Urava then interviews you, one question at a time.',
    },
    'drafting.2': {
      title: 'Attach what you received',
      body: "Attach the notice or plaint you're answering. Urava reads it and asks only what's missing.",
    },
    'coach.assistant': {
      title: 'Ask in your own words',
      body: 'Speak or type in Malayalam, Hindi or English. It can create cases and log outcomes — you confirm before anything is saved.',
    },
    'coach.provenance': {
      title: 'Check this hearing',
      body: 'This hearing came from eCourts. Court data can lag — tap to confirm it.',
    },
    'coach.cmdk': {
      title: 'Jump anywhere',
      body: 'Press Ctrl+K to jump to any case, memo or client.',
    },
  },
};

const hi: TourStrings = {
  chrome: {
    next: 'आगे',
    prev: 'पीछे',
    done: 'समाप्त',
    closeAria: 'टूर बंद करें',
    progress: 'चरण {{current}} / {{total}}',
    announceStep: (n, m, title) => `चरण ${n} / ${m}: ${title}`,
    resumePrompt: (n, m) => `टूर फिर शुरू करें? (चरण ${n} / ${m})`,
    resume: 'फिर शुरू करें',
    notNow: 'अभी नहीं',
    skipTitle: 'टूर रोकें?',
    skipBody: 'आपकी जगह सहेज ली गई है — Settings → Help से कभी भी फिर शुरू करें।',
    keepGoing: 'जारी रखें',
    stopForNow: 'अभी रोकें',
    dontShowAgain: 'सुझाव दोबारा न दिखाएं',
    skipChapter: 'यह हिस्सा छोड़ें',
    chapterProgress: (chapter) => `${chapter} · {{current}} / {{total}}`,
  },
  chapters: {
    day: 'आपका दिन',
    research: 'रिसर्च',
    drafting: 'मसौदा',
    cases: 'आपके केस',
    always: 'हमेशा उपलब्ध',
  },
  steps: {
    'around.1': {
      title: 'आपकी सुबह यहीं से शुरू होती है',
      body: 'आज और कल की सुनवाइयां, अदालत से पहले करने वाले कॉल, और आने वाले काम — हर सुबह यहां तैयार मिलते हैं, जैसे आपके लिए बनी कॉज़ लिस्ट (cause list)। अदालत के बाद नतीजा दर्ज करें, और Urava अगली सुनवाई आपकी डायरी में खुद लिख देता है।',
    },
    'around.2': {
      title: 'हर काम की शुरुआत यहीं से',
      body: 'हर नए काम के लिए एक बटन: पूरे उद्धरणों (citations) वाला रिसर्च मेमोरेंडम, 17 में से कोई भी अदालती दस्तावेज़ (document), या नई केस फ़ाइल (case file)। FIR, नोटिस और फ़ैसले सीधे चैट में अटैच करें — Urava मलयालम, हिंदी और अंग्रेज़ी में स्कैन किए और हाथ से लिखे दस्तावेज़ भी पढ़ लेता है।',
    },
    'around.3': {
      title: 'अपने शब्दों में बताइए',
      body: 'स्थिति वैसे लिखें जैसे किसी सहकर्मी को बताते हैं — अंग्रेज़ी, हिंदी या मलयालम में — या माइक दबाकर बोलें। Urava ज़रूरी बातें पूछता है, फिर रिसर्च लायक सटीक कानूनी सवाल तैयार करता है। आपकी मंज़ूरी के बिना कुछ नहीं चलता।',
    },
    'around.4': {
      title: 'कागज़ात इसे थमा दें',
      body: 'FIR, नोटिस, अनुबंध या निर्णय यहां जोड़ें। Urava स्कैन किए और हाथ से लिखे दस्तावेज़ पढ़ता है और उनमें जो सच में लिखा है — तारीखें, पक्षकार, रकम — उसी के आधार पर रिसर्च करता है, सिर्फ आपके सारांश पर नहीं।',
    },
    'around.5': {
      title: 'सवाल पहले से पता है?',
      body: 'बातचीत ज़रूरी नहीं। जैसे ही आप सवाल लिखते हैं, नीचे "जैसा लिखा है वैसा ही रिसर्च करें" बटन दिखता है — एक टैप में आपके शब्द बिना बदले चलते हैं, कोई पूछताछ नहीं।',
    },
    'around.6': {
      title: 'आपकी हर रिसर्च, एक जगह',
      body: 'आपकी रिसर्च लाइब्रेरी — सवाल, मेमो के टेक्स्ट या केस से खोजें, चाहे वह किसी केस में दर्ज हो या नहीं। हर मेमो के उद्धरण (citations) लाइव स्रोतों से जांचे जाते हैं, और बिना जांचे लिंक चिह्नित होते हैं ताकि आप जानें कि क्या दोबारा पढ़ना है।',
    },
    'around.7': {
      title: 'दस्तावेज़ चुनें, सवालों के जवाब दें',
      body: 'ज़मानत अर्ज़ी, धारा 138 नोटिस, वाद, लिखित बयान, उपभोक्ता शिकायत, रिट याचिका — 17 अदालती प्रारूप। एक चुनें, और Urava एक-एक करके सवाल पूछता है — जो आप पहले बता चुके हैं, वह दोबारा नहीं पूछा जाता।',
    },
    'around.8': {
      title: 'जो मिला है, उसे जोड़ दें',
      body: 'किसी नोटिस या वाद का जवाब दे रहे हैं? उसे इंटरव्यू के दौरान जोड़ें — Urava पक्षकार, तारीखें और रकम दस्तावेज़ से खुद निकाल लेता है। आपसे सिर्फ वही पूछा जाता है जो सच में छूटा हो।',
    },
    'around.9': {
      title: 'हर केस की एक फ़ाइल',
      body: 'हर केस अपनी सुनवाइयां, रिसर्च, मसौदे, दस्तावेज़, काम और लोग एक साथ रखता है — आपकी असली केस फ़ाइल, बिना खोए पन्नों के। केस खोलें और उसका सब कुछ सामने होता है।',
    },
    'around.10': {
      title: 'केस में दर्ज करना ज़रूरी नहीं',
      body: 'छोटे सवाल के लिए केस नहीं चाहिए — रिसर्च और मसौदे अपने आप में पूरे हैं। जब कोई बात असली मुकदमा बन जाए, तो यहां केस बनाएं और पुराना काम एक टैप में उसमें दर्ज करें। दोनों तरह से कुछ नहीं खोता।',
    },
    'around.11': {
      title: 'किसी भी पेज से पूछें',
      body: 'मसौदे के बीच में, मेमो पढ़ते हुए, केस देखते हुए — यह बटन कहीं से भी रिसर्च चैट खोलता है। डेस्कटॉप पर यह आपके काम के बगल में खुलता है, ताकि जवाब मिलते समय आपकी जगह न छूटे।',
    },
    'around.12': {
      title: 'आपके प्लान में क्या शामिल है',
      body: 'इस महीने बची हुई रिसर्च हमेशा यहां दिखती है। सिर्फ सफल रिसर्च गिनी जाती है — असफल रन कभी आपके खाते से नहीं कटता। मसौदों का अपना अलग कोटा है, और केस ट्रैकिंग कभी गिनी ही नहीं जाती।',
    },
    'around.13': {
      title: 'सेटिंग्स, मदद और WhatsApp',
      body: 'आपका मेन्यू: Settings, यह टूर जब चाहें दोबारा, Urava कैसे काम करता है इसकी पूरी गाइड, और WhatsApp पर Urava — जिस नंबर से आप रोज़ काम करते हैं, उसी से सवाल पूछें और सुनवाई के रिमाइंडर पाएं।',
    },
    'memo.1': {
      title: 'उद्धरण जांचे जाते हैं',
      body: 'हम हर उद्धृत लिंक (citation link) की जांच करते हैं। बिना जांचे लिंक परिशिष्ट में चिह्नित होते हैं। भरोसा करने से पहले निर्णय खुद ज़रूर पढ़ें।',
    },
    'memo.2': {
      title: 'अपने मेमो के साथ काम करें',
      body: 'इस पर चर्चा करें, सुनवाई से पहले खुद को परखें, या Urava से विरोधी पक्ष की दलील सुनें।',
    },
    'memo.3': {
      title: 'रिसर्च से मसौदा बनाएं',
      body: 'यहीं से नोटिस या याचिका का मसौदा बनाएं — यह केवल इसी मेमो के प्रमाणों का हवाला देता है।',
    },
    'casefile.1': {
      title: 'एक केस, सब कुछ उसमें',
      body: 'इस केस की सुनवाइयां, रिसर्च, मसौदे, दस्तावेज़, काम और लोग — हर एक अपने टैब में।',
    },
    'casefile.2': {
      title: 'केस से ही काम करें',
      body: 'यहीं से इस मुद्दे पर रिसर्च करें या इस केस के लिए मसौदा बनाएं। नतीजे अपने आप इसी केस से जुड़ जाते हैं।',
    },
    'casefile.3': {
      title: 'अदालत के बाद, नतीजा दर्ज करें',
      body: 'नतीजा और अगली तारीख भरें। Urava अगली सुनवाई आपकी डायरी में खुद जोड़ देता है।',
    },
    'drafting.1': {
      title: 'दस्तावेज़ चुनें',
      body: 'नोटिस, ज़मानत अर्ज़ी, वाद, जवाब — 17 प्रकार। फिर Urava एक-एक करके सवाल पूछता है।',
    },
    'drafting.2': {
      title: 'जो मिला है उसे जोड़ें',
      body: 'जिस नोटिस या वाद का जवाब देना है, उसे जोड़ें। Urava उसे पढ़ता है और केवल छूटी हुई जानकारी पूछता है।',
    },
    'coach.assistant': {
      title: 'अपनी भाषा में पूछें',
      body: 'मलयालम, हिंदी या अंग्रेज़ी में बोलें या लिखें। यह केस बना सकता है और नतीजे दर्ज कर सकता है — सहेजने से पहले आपकी पुष्टि ज़रूरी है।',
    },
    'coach.provenance': {
      title: 'इस सुनवाई को जांचें',
      body: 'यह सुनवाई eCourts से आई है। अदालत का डेटा देर से बदल सकता है — पुष्टि के लिए टैप करें।',
    },
    'coach.cmdk': {
      title: 'कहीं भी तुरंत जाएं',
      body: 'किसी भी केस, मेमो या मुवक्किल पर जाने के लिए Ctrl+K दबाएं।',
    },
  },
};

const ml: TourStrings = {
  chrome: {
    next: 'അടുത്തത്',
    prev: 'പിന്നോട്ട്',
    done: 'കഴിഞ്ഞു',
    closeAria: 'ടൂർ അടയ്ക്കുക',
    progress: 'ഘട്ടം {{current}} / {{total}}',
    announceStep: (n, m, title) => `ഘട്ടം ${n} / ${m}: ${title}`,
    resumePrompt: (n, m) => `ടൂർ തുടരണോ? (ഘട്ടം ${n} / ${m})`,
    resume: 'തുടരുക',
    notNow: 'ഇപ്പോൾ വേണ്ട',
    skipTitle: 'ടൂർ നിർത്തണോ?',
    skipBody: 'നിങ്ങളുടെ സ്ഥാനം സേവ് ചെയ്തു — Settings → Help-ൽ നിന്ന് എപ്പോൾ വേണമെങ്കിലും തുടരാം.',
    keepGoing: 'തുടരട്ടെ',
    stopForNow: 'തൽക്കാലം നിർത്തുക',
    dontShowAgain: 'ഇനി നുറുങ്ങുകൾ കാണിക്കേണ്ട',
    skipChapter: 'ഈ ഭാഗം ഒഴിവാക്കുക',
    chapterProgress: (chapter) => `${chapter} · {{current}} / {{total}}`,
  },
  chapters: {
    day: 'നിങ്ങളുടെ ദിവസം',
    research: 'റിസർച്ച്',
    drafting: 'കരട്',
    cases: 'നിങ്ങളുടെ കേസുകൾ',
    always: 'എപ്പോഴും ലഭ്യം',
  },
  steps: {
    'around.1': {
      title: 'നിങ്ങളുടെ പ്രഭാതം ഇവിടെ തുടങ്ങുന്നു',
      body: 'ഇന്നത്തെയും നാളത്തെയും ഹിയറിംഗുകൾ, കോടതിക്കു മുമ്പ് വിളിക്കേണ്ട കക്ഷികൾ, തീരേണ്ട ജോലികൾ — എല്ലാ രാവിലെയും ഇവിടെ തയ്യാറായിരിക്കും, നിങ്ങൾക്കായി ഒരുക്കിയ കോസ് ലിസ്റ്റ് (cause list) പോലെ. കോടതി കഴിഞ്ഞ് ഫലം രേഖപ്പെടുത്തിയാൽ അടുത്ത ഹിയറിംഗ് Urava തനിയെ ഡയറിയിൽ ചേർക്കും.',
    },
    'around.2': {
      title: 'എല്ലാ ജോലിയും ഇവിടെ തുടങ്ങുന്നു',
      body: 'എല്ലാ പുതിയ ജോലിക്കും ഒരൊറ്റ ബട്ടൺ: പൂർണ്ണ ഉദ്ധരണികളോടെയുള്ള (citations) റിസർച്ച് മെമ്മോറാണ്ടം, 17 തരം കോടതി രേഖകളിൽ (documents) ഏതും, അല്ലെങ്കിൽ പുതിയ കേസ് ഫയൽ (case file). FIR, നോട്ടീസ്, വിധികൾ എന്നിവ ചാറ്റിനുള്ളിൽ തന്നെ അറ്റാച്ച് ചെയ്യാം — സ്കാൻ ചെയ്തതും കൈയെഴുത്തുള്ളതുമായ രേഖകൾ മലയാളത്തിലും ഹിന്ദിയിലും ഇംഗ്ലീഷിലും Urava വായിക്കും.',
    },
    'around.3': {
      title: 'സ്വന്തം വാക്കുകളിൽ പറയൂ',
      body: 'ഒരു സഹപ്രവർത്തകനോട് പറയുന്നതുപോലെ സാഹചര്യം എഴുതുക — ഇംഗ്ലീഷിലോ ഹിന്ദിയിലോ മലയാളത്തിലോ — അല്ലെങ്കിൽ മൈക്ക് തൊട്ട് സംസാരിക്കുക. Urava പ്രധാന കാര്യങ്ങൾ ചോദിച്ചറിഞ്ഞ്, റിസർച്ചിന് പറ്റിയ കൃത്യമായ നിയമ ചോദ്യം തയ്യാറാക്കും. നിങ്ങളുടെ അംഗീകാരമില്ലാതെ ഒന്നും ഓടില്ല.',
    },
    'around.4': {
      title: 'രേഖകൾ ഇതിന് കൈമാറൂ',
      body: 'FIR, നോട്ടീസ്, കരാർ, വിധി — ഇവിടെ ചേർക്കുക. സ്കാൻ ചെയ്തതും കൈയെഴുത്തുള്ളതുമായ രേഖകൾ Urava വായിച്ച്, അവയിൽ യഥാർത്ഥത്തിൽ എഴുതിയിരിക്കുന്നതിന്റെ — തീയതികൾ, കക്ഷികൾ, തുകകൾ — അടിസ്ഥാനത്തിൽ റിസർച്ച് ചെയ്യും, നിങ്ങളുടെ സംഗ്രഹം മാത്രം നോക്കിയല്ല.',
    },
    'around.5': {
      title: 'ചോദ്യം നേരത്തേ അറിയാമോ?',
      body: 'ചാറ്റ് നിർബന്ധമില്ല. ചോദ്യം ടൈപ്പ് ചെയ്ത ഉടൻ, കമ്പോസറിനു താഴെ "എഴുതിയതുപോലെ തന്നെ റിസർച്ച് ചെയ്യുക" ബട്ടൺ വരും — ഒരൊറ്റ ടാപ്പിൽ നിങ്ങളുടെ വാക്കുകൾ മാറ്റമില്ലാതെ ഓടും, കൂടുതൽ ചോദ്യങ്ങളില്ല.',
    },
    'around.6': {
      title: 'നിങ്ങൾ നടത്തിയ എല്ലാ റിസർച്ചും',
      body: 'നിങ്ങളുടെ റിസർച്ച് ലൈബ്രറി — ചോദ്യം, മെമ്മോയിലെ വാചകം, കേസ് എന്നിവ വെച്ച് തിരയാം; കേസിൽ ഫയൽ ചെയ്തിട്ടുണ്ടോ എന്നത് പ്രശ്നമല്ല. ഓരോ മെമ്മോയുടെയും ഉദ്ധരണികൾ (citations) ലൈവ് സ്രോതസ്സുകളുമായി പരിശോധിക്കുന്നു; പരിശോധിക്കാനാവാത്തവ അടയാളപ്പെടുത്തും.',
    },
    'around.7': {
      title: 'രേഖ തിരഞ്ഞെടുക്കൂ, ചോദ്യങ്ങൾക്ക് ഉത്തരം നൽകൂ',
      body: 'ജാമ്യാപേക്ഷകൾ, വകുപ്പ് 138 നോട്ടീസുകൾ, വാദപത്രങ്ങൾ, ലിഖിത പ്രസ്താവനകൾ, ഉപഭോക്തൃ പരാതികൾ, റിട്ട് ഹർജികൾ — 17 കോടതി ഫോർമാറ്റുകൾ. ഒന്ന് തിരഞ്ഞെടുത്താൽ Urava ഓരോ ചോദ്യമായി ചോദിക്കും — നേരത്തേ പറഞ്ഞത് വീണ്ടും ചോദിക്കില്ല.',
    },
    'around.8': {
      title: 'കിട്ടിയത് ചേർക്കൂ',
      body: 'ഒരു നോട്ടീസിനോ വാദപത്രത്തിനോ മറുപടി തയ്യാറാക്കുകയാണോ? ഇന്റർവ്യൂവിനിടെ അത് ചേർക്കുക — കക്ഷികൾ, തീയതികൾ, തുകകൾ എന്നിവ Urava രേഖയിൽ നിന്നു തന്നെ എടുക്കും. ശരിക്കും വിട്ടുപോയത് മാത്രമേ നിങ്ങളോട് ചോദിക്കൂ.',
    },
    'around.9': {
      title: 'ഓരോ കേസിനും ഒരു ഫയൽ',
      body: 'ഓരോ കേസും അതിന്റെ ഹിയറിംഗുകൾ, റിസർച്ച്, കരടുകൾ, രേഖകൾ, ജോലികൾ, ആളുകൾ — എല്ലാം ഒരുമിച്ചു സൂക്ഷിക്കുന്നു; താളുകൾ നഷ്ടപ്പെടാത്ത നിങ്ങളുടെ കേസ് ഫയൽ. കേസ് തുറന്നാൽ അതിന്റെ എല്ലാം മുന്നിൽ.',
    },
    'around.10': {
      title: 'കേസിൽ ഫയൽ ചെയ്യണമെന്നില്ല',
      body: 'വേഗത്തിലുള്ള ഒരു ചോദ്യത്തിന് കേസ് വേണ്ട — റിസർച്ചും കരടുകളും സ്വന്തമായി നിൽക്കും. എന്തെങ്കിലും യഥാർത്ഥ വ്യവഹാരമായാൽ ഇവിടെ കേസ് ഉണ്ടാക്കി, പഴയ ജോലി ഒരൊറ്റ ടാപ്പിൽ അതിൽ ചേർക്കാം. രണ്ടായാലും ഒന്നും നഷ്ടപ്പെടില്ല.',
    },
    'around.11': {
      title: 'ഏതു പേജിൽ നിന്നും ചോദിക്കാം',
      body: 'കരടിന്റെ നടുവിൽ, മെമ്മോ വായിക്കുമ്പോൾ, കേസ് നോക്കുമ്പോൾ — ഈ ബട്ടൺ എവിടെ നിന്നും റിസർച്ച് ചാറ്റ് തുറക്കും. ഡെസ്ക്ടോപ്പിൽ അത് നിങ്ങളുടെ ജോലിയുടെ അരികിൽ തുറക്കുന്നു — ഉത്തരം കിട്ടുമ്പോഴും നിങ്ങളുടെ സ്ഥാനം നഷ്ടപ്പെടില്ല.',
    },
    'around.12': {
      title: 'നിങ്ങളുടെ പ്ലാനിൽ എന്തുണ്ട്',
      body: 'ഈ മാസം ബാക്കിയുള്ള റിസർച്ചുകൾ എപ്പോഴും ഇവിടെ കാണാം. വിജയിച്ച റിസർച്ച് മാത്രമേ എണ്ണൂ — പരാജയപ്പെട്ട റൺ ഒരിക്കലും നിങ്ങളുടെ കണക്കിൽ കുറയ്ക്കില്ല. കരടുകൾക്ക് വേറെ ക്വോട്ടയാണ്; കേസ് ട്രാക്കിംഗ് ഒരിക്കലും എണ്ണുന്നില്ല.',
    },
    'around.13': {
      title: 'സെറ്റിംഗ്സ്, സഹായം, WhatsApp',
      body: 'നിങ്ങളുടെ മെനു: Settings, ഈ ടൂർ എപ്പോൾ വേണമെങ്കിലും വീണ്ടും, Urava എങ്ങനെ പ്രവർത്തിക്കുന്നു എന്ന പൂർണ്ണ ഗൈഡ്, WhatsApp-ൽ Urava — ദിവസവും ഉപയോഗിക്കുന്ന അതേ നമ്പറിൽ നിന്ന് ചോദ്യങ്ങൾ ചോദിക്കാം, ഹിയറിംഗ് റിമൈൻഡറുകൾ നേടാം.',
    },
    'memo.1': {
      title: 'ഉദ്ധരണികൾ പരിശോധിക്കുന്നു',
      body: 'ഉദ്ധരിച്ച ഓരോ ലിങ്കും (citation link) ഞങ്ങൾ പരിശോധിക്കുന്നു. പരിശോധിക്കാത്തവ അനുബന്ധത്തിൽ അടയാളപ്പെടുത്തും. ആശ്രയിക്കും മുമ്പ് വിധി സ്വയം വായിക്കുക.',
    },
    'memo.2': {
      title: 'മെമ്മോയുമായി പ്രവർത്തിക്കുക',
      body: 'ഇതേക്കുറിച്ച് ചർച്ച ചെയ്യുക, ഹിയറിംഗിനു മുമ്പ് സ്വയം പരീക്ഷിക്കുക, അല്ലെങ്കിൽ എതിർഭാഗത്തിന്റെ വാദം Urava-യോട് ഉന്നയിക്കാൻ പറയുക.',
    },
    'memo.3': {
      title: 'റിസർച്ചിൽ നിന്ന് കരടിലേക്ക്',
      body: 'നോട്ടീസോ ഹർജിയോ ഇവിടെ തയ്യാറാക്കുക — ഈ മെമ്മോയിലെ ആധികാരികതകൾ മാത്രമേ അതിൽ ഉദ്ധരിക്കൂ.',
    },
    'casefile.1': {
      title: 'ഒരു കേസ്, എല്ലാം അതിൽ',
      body: 'ഈ കേസിന്റെ ഹിയറിംഗുകൾ, റിസർച്ച്, കരടുകൾ, രേഖകൾ, ജോലികൾ, ആളുകൾ — ഓരോന്നും അതതു ടാബിൽ.',
    },
    'casefile.2': {
      title: 'കേസിൽ നിന്നു തന്നെ പ്രവർത്തിക്കുക',
      body: 'ഈ വിഷയം റിസർച്ച് ചെയ്യാനും ഈ കേസിനു വേണ്ടി കരട് തയ്യാറാക്കാനും ഇവിടെ നിന്നു കഴിയും. ഫലങ്ങൾ തനിയെ ഈ കേസിൽ ചേരും.',
    },
    'casefile.3': {
      title: 'കോടതിക്കു ശേഷം ഫലം രേഖപ്പെടുത്തുക',
      body: 'ഫലവും അടുത്ത തീയതിയും നൽകുക. അടുത്ത ഹിയറിംഗ് Urava നിങ്ങളുടെ ഡയറിയിൽ തനിയെ ചേർക്കും.',
    },
    'drafting.1': {
      title: 'രേഖ തിരഞ്ഞെടുക്കുക',
      body: 'നോട്ടീസുകൾ, ജാമ്യാപേക്ഷകൾ, വാദപത്രങ്ങൾ, മറുപടികൾ — 17 തരം. പിന്നെ Urava ഓരോ ചോദ്യമായി ചോദിക്കും.',
    },
    'drafting.2': {
      title: 'കിട്ടിയത് ചേർക്കുക',
      body: 'മറുപടി നൽകേണ്ട നോട്ടീസോ വാദപത്രമോ ചേർക്കുക. Urava അതു വായിച്ച് വിട്ടുപോയ വിവരങ്ങൾ മാത്രം ചോദിക്കും.',
    },
    'coach.assistant': {
      title: 'സ്വന്തം ഭാഷയിൽ ചോദിക്കുക',
      body: 'മലയാളത്തിലോ ഹിന്ദിയിലോ ഇംഗ്ലീഷിലോ പറയുകയോ ടൈപ്പ് ചെയ്യുകയോ ചെയ്യുക. കേസുകൾ സൃഷ്ടിക്കാനും ഫലങ്ങൾ രേഖപ്പെടുത്താനും ഇതിനു കഴിയും — സേവ് ചെയ്യും മുമ്പ് നിങ്ങളുടെ സ്ഥിരീകരണം വേണം.',
    },
    'coach.provenance': {
      title: 'ഈ ഹിയറിംഗ് പരിശോധിക്കുക',
      body: 'ഈ ഹിയറിംഗ് eCourts-ൽ നിന്നാണ്. കോടതി ഡേറ്റ വൈകിയേക്കാം — സ്ഥിരീകരിക്കാൻ ടാപ്പ് ചെയ്യുക.',
    },
    'coach.cmdk': {
      title: 'എവിടേക്കും വേഗം പോകാം',
      body: 'ഏതു കേസിലേക്കോ മെമ്മോയിലേക്കോ കക്ഷിയിലേക്കോ പോകാൻ Ctrl+K അമർത്തുക.',
    },
  },
};

export const STRINGS: Record<TourLang, TourStrings> = { en, hi, ml };

/** voice_lang (MicButton) → navigator.language → English (spec §8). */
export function resolveTourLang(): TourLang {
  try {
    const saved = localStorage.getItem('voice_lang');
    if (saved === 'hi-IN') return 'hi';
    if (saved === 'ml-IN') return 'ml';
    if (saved === 'en-IN') return 'en';
  } catch {
    /* fall through */
  }
  const nav = (navigator.language || '').toLowerCase();
  if (nav.startsWith('hi')) return 'hi';
  if (nav.startsWith('ml')) return 'ml';
  return 'en';
}
